import React, { useCallback, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import { makeStyles } from "@material-ui/core/styles";
import Loading from "../../../../common/Loading";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";

import Button from "components/CustomButtons/Button";
import { Box, Divider, InputAdornment } from "@material-ui/core";

const validationSchema = Yup.object().shape({
  description: Yup.string().required("Description is required"),
  amount: Yup.number().min(0, "Min value 0.").required("Amount is required"),
  printing: Yup.number().min(0, "Min value 0."),
  other: Yup.number().min(0, "Min value 0."),
  pages: Yup.number().min(0, "Min value 0."),
  adPages: Yup.number().min(0, "Min value 0."),
  staffPercent: Yup.number().min(0, "Min value 0.")
});

const useStyles = makeStyles(theme => ({
  dialogActions: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2)
  },
  marginBottom: {
    marginBottom: theme.spacing(3)
  },
  marginRight: {
    marginRight: theme.spacing(3)
  },
  content: {
    minHeight: 100
  }
}));

const defaultValues = {
  description: "",
  printing: 0,
  other: 0,
  pages: 0,
  adPages: 0,
  staffPercent: 0,
  amount: 0
};

const NewAdvertisingIncomeDialog = ({ open, onClose, onSave }) => {
  const classes = useStyles();

  const handleSubmit = useCallback(values => {
    setDirty(true);
    onSave(values);
  }, []);

  const [isDirty, setDirty] = useState(false);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Chapter Financial Information</DialogTitle>
      <DialogContent>
        <Grid container direction="column">
          <Typography className={classes.marginBottom}>
            <strong>Add Advertising Income</strong>
          </Typography>
          <Formik
            initialValues={defaultValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              touched,
              errors,
              handleChange,
              handleBlur,
              isValid
            }) => (
              <Form autoComplete="off">
                <Grid item>
                  <Grid container spacing={2}>
                    <Grid item md={10} sm={6} xs={12}>
                      <TextField
                        error={Boolean(
                          touched.description && errors.description
                        )}
                        helperText={touched.description && errors.description}
                        label="Description"
                        variant="outlined"
                        name="description"
                        fullWidth
                        value={values.description}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item md={2} sm={12} xs={12}>
                      <TextField
                        error={Boolean(touched.amount && errors.amount)}
                        helperText={touched.amount && errors.amount}
                        fullWidth
                        label="Amount"
                        variant="outlined"
                        name="amount"
                        value={values.amount}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          )
                        }}
                        onChange={handleChange}
                      />
                    </Grid>
                  </Grid>
                  <Box mb={2} mt={2}>
                    <Divider />
                  </Box>
                  <Grid container spacing={2} className={classes.content}>
                    <Grid item xs={12}>
                      <TextField
                        error={Boolean(touched.printing && errors.printing)}
                        helperText={touched.printing && errors.printing}
                        label="Total printing, mailing and other direct expenses to produce"
                        variant="outlined"
                        name="printing"
                        fullWidth
                        value={values.printing}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        error={Boolean(touched.other && errors.other)}
                        helperText={touched.other && errors.other}
                        label="Other expenses directly related to production"
                        variant="outlined"
                        name="other"
                        fullWidth
                        value={values.other}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        error={Boolean(touched.pages && errors.pages)}
                        helperText={touched.pages && errors.pages}
                        label="Total pages of publication/website"
                        variant="outlined"
                        name="pages"
                        fullWidth
                        value={values.pages}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        error={Boolean(touched.adPages && errors.adPages)}
                        helperText={touched.adPages && errors.adPages}
                        label="Total number of ad pages in publication/website"
                        variant="outlined"
                        name="adPages"
                        fullWidth
                        value={values.adPages}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        error={Boolean(
                          touched.staffPercent && errors.staffPercent
                        )}
                        helperText={touched.staffPercent && errors.staffPercent}
                        label="Percent of paid staff time spent on publication/website"
                        variant="outlined"
                        name="staffPercent"
                        fullWidth
                        value={values.staffPercent}
                        onChange={handleChange}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <div className={classes.dialogActions}>
                  {isDirty ? (
                    <Loading />
                  ) : (
                    <>
                      <Button
                        className={classes.marginRight}
                        color="success"
                        variant="contained"
                        type="submit"
                      >
                        Submit
                      </Button>
                      <Button
                        color="danger"
                        variant="contained"
                        onClick={onClose}
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default NewAdvertisingIncomeDialog;
